import { useTranslation } from "react-i18next";
import { ArrowRight, Check, Copy, Download, Printer } from "lucide-react";
import { toast } from "sonner";
import { Badge, Btn } from "./primitives";

export interface RecoveryCodesProps {
  /** The one-time recovery codes from `POST /mfa/totp/confirm/`. */
  codes: ReadonlyArray<string>;
  /** "I've saved these codes" checkbox state. */
  saved: boolean;
  onSaved: (next: boolean) => void;
  /** Sign-in address, written into the downloaded/printed copy so it says whose codes they are. */
  account?: string;
  /** Base name of the downloaded file (no extension). Default "we-co-recovery-codes". */
  fileName?: string;
  /** Continue handler. When given, a Continue button is rendered, disabled until `saved`. */
  onContinue?: () => void;
  continueLabel?: string;
  busy?: boolean;
  /** Show the design's header (badge + title + explanation). Default true. */
  showHeader?: boolean;
}

/** Plain-text copy of the codes: header lines, then "01  XXXX-XXXX" per code. */
export function recoveryCodesText(codes: ReadonlyArray<string>, account?: string): string {
  const lines = ["We-Co Suite recovery codes"];
  if (account) lines.push(account);
  lines.push("Each code works once.", "");
  codes.forEach((c, i) => lines.push(`${String(i + 1).padStart(2, "0")}  ${c}`));
  return `${lines.join("\n")}\n`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[ch]!);
}

/**
 * Recovery-codes grid with real Download (.txt) / Copy / Print actions and
 * the "I've saved these codes" gate — the design's ScreenRecovery body
 * (ob-entry.jsx).
 */
export function RecoveryCodes({
  codes,
  saved,
  onSaved,
  account,
  fileName = "we-co-recovery-codes",
  onContinue,
  continueLabel,
  busy,
  showHeader = true,
}: RecoveryCodesProps) {
  const { t } = useTranslation();
  const text = recoveryCodesText(codes, account);

  const download = () => {
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileName}.txt`;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke on the next tick: some browsers start the download asynchronously.
    setTimeout(() => URL.revokeObjectURL(url), 0);
    toast.success(t("onboarding.kit.recovery.downloaded", { defaultValue: "Codes downloaded as {{file}}", file: `${fileName}.txt` }));
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("onboarding.kit.recovery.copied", "Codes copied"));
    } catch {
      toast.error(t("onboarding.kit.copyFailed", "Couldn't copy. Select the text and copy it instead."));
    }
  };

  const print = () => {
    const w = window.open("", "_blank", "width=480,height=640");
    if (!w) {
      toast.error(t("onboarding.kit.recovery.printBlocked", "Your browser blocked the print window. Allow pop-ups, or use Download."));
      return;
    }
    const items = codes.map((c) => `<li>${escapeHtml(c)}</li>`).join("");
    w.document.write(
      `<!doctype html><html><head><meta charset="utf-8"><title>We-Co recovery codes</title>` +
        `<style>body{font-family:ui-monospace,Menlo,Consolas,monospace;margin:32px;color:#0F172A}` +
        `h1{font:600 16px system-ui,sans-serif;margin:0 0 4px}p{font:13px system-ui,sans-serif;color:#475569;margin:0 0 16px}` +
        `ol{columns:2;font-size:15px;line-height:2;padding-left:28px}</style></head><body>` +
        `<h1>We-Co Suite recovery codes</h1>` +
        (account ? `<p>${escapeHtml(account)}</p>` : "") +
        `<p>Each code works once.</p><ol>${items}</ol></body></html>`,
    );
    w.document.close();
    w.focus();
    // Close only after printing — closing right after print() cancels the
    // dialog in browsers where print() does not block (Safari).
    w.onafterprint = () => w.close();
    w.print();
  };

  return (
    <section
      className="ob-glass"
      style={{ padding: "clamp(22px,4cqi,36px)", display: "flex", flexDirection: "column", gap: 18 }}
    >
      {showHeader && (
        <>
          <span>
            <Badge kind="ok" icon={Check}>
              {t("onboarding.kit.recovery.mfaOn", "Two-step verification on")}
            </Badge>
          </span>
          <h1 className="ob-h2" style={{ fontSize: 26 }}>
            {t("onboarding.kit.recovery.title", "Save your recovery codes.")}
          </h1>
          <p className="ob-p">
            {t(
              "onboarding.kit.recovery.body",
              "Each code works once. Keep them outside We-Co, in a password manager or printed and stored somewhere safe.",
            )}
          </p>
        </>
      )}
      <ol className="rc-grid ob-mono" dir="ltr" aria-label={t("onboarding.kit.recovery.listLabel", "Recovery codes")}>
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ol>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        <Btn kind="sec" size="m" icon={Download} onClick={download}>
          {t("onboarding.kit.recovery.download", "Download")}
        </Btn>
        <Btn kind="sec" size="m" icon={Copy} onClick={copy}>
          {t("onboarding.kit.recovery.copy", "Copy")}
        </Btn>
        <Btn kind="sec" size="m" icon={Printer} onClick={print}>
          {t("onboarding.kit.recovery.print", "Print")}
        </Btn>
      </div>
      <label
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "12px 14px",
          borderRadius: 14,
          background: saved ? "rgba(16,185,129,.1)" : "rgba(148,163,184,.12)",
          cursor: "pointer",
          fontSize: 14,
          fontWeight: 500,
          color: "var(--ink)",
        }}
      >
        <input
          type="checkbox"
          checked={saved}
          onChange={(e) => onSaved(e.target.checked)}
          style={{ width: 20, height: 20, accentColor: "#1F47F5", flex: "none" }}
        />
        {t("onboarding.kit.recovery.saved", "I've saved these codes somewhere safe")}
      </label>
      {onContinue && (
        <div>
          <Btn size="l" disabled={!saved} busy={busy} onClick={onContinue} iconEnd={ArrowRight} flipEnd>
            {continueLabel ?? t("onboarding.kit.continue", "Continue")}
          </Btn>
        </div>
      )}
    </section>
  );
}
